// components/LanguageSelector.js
import React, { useState, useEffect, useRef } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import styles from './LanguageSelector.module.css';

const LanguageSelector = ({ value, onChange, autoDetect = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(value || 'fr');
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'fr', name: 'English', nativeName: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', nativeName: 'English', flag: 'ᴇɴ' },
  ];

  const detectBrowserLanguage = () => {
    const browserLang = navigator.language || navigator.userLanguage;
    const mainLangCode = (browserLang || 'en').split('-')[0].toLowerCase();
    return mainLangCode === 'fr' ? 'fr' : 'en';
  };

  useEffect(() => {
    if (autoDetect && !value) {
      const detected = detectBrowserLanguage();
      setSelectedLanguage(detected);
      onChange?.(detected);
    }
  }, [autoDetect, value, onChange]);

  useEffect(() => {
    if (value) setSelectedLanguage(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selected = languages.find(l => l.code === selectedLanguage) || languages[0];

  const handleSelect = (code) => {
    setSelectedLanguage(code);
    setIsOpen(false);
    onChange?.(code);
  };

  return (
    <div className={styles.container}>
      <label className={styles.label}>
        <Globe size={18} />
        <span>Langue de l'interface</span>
      </label>

      <div className={styles.dropdown} ref={dropdownRef}>
        <button
          type="button"
          className={styles.dropdownButton}
          onClick={() => setIsOpen(v => !v)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className={styles.selected}>
            <span className={styles.flag}>{selected.flag}</span>
            <span className={styles.name}>{selected.nativeName}</span>
          </div>
          <ChevronDown size={20} className={`${styles.icon} ${isOpen ? styles.iconOpen : ''}`} />
        </button>

        {isOpen && (
          <div className={styles.menu} role="listbox">
            {languages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`${styles.option} ${selectedLanguage === lang.code ? styles.optionSelected : ''}`}
                onClick={() => handleSelect(lang.code)}
                role="option"
                aria-selected={selectedLanguage === lang.code}
              >
                <span className={styles.flag}>{lang.flag}</span>
                <div className={styles.info}>
                  <span className={styles.native}>{lang.nativeName}</span>
                  <span className={styles.english}>{lang.name}</span>
                </div>
                {selectedLanguage === lang.code && <span className={styles.check}>✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <p className={styles.hint}>
        {autoDetect && !value && <span className={styles.autoBadge}>🎯 Détection automatique</span>}
        Vous pourrez changer la langue plus tard dans vos paramètres
      </p>
    </div>
  );
};

export default LanguageSelector;
